/**
 * Boot the harness client from Orik's own page.
 *
 * The in-app mount (`harness/in-app.ts`) is a lazy chunk of this same Vite build: `harness/vite-plugin.ts`
 * gives the build the defines, alias and loader transform their own Vite config uses, so the shell is
 * bundled with Orik rather than fetched as a separate artifact, and it shares React and the rest of the
 * app's dependencies instead of carrying its own copies. The chunk's hashed name versions it, so a deploy
 * is a new URL and nothing here has to stamp a revision on one.
 *
 * The harness renders the signed-in page, so it boots into the element the app gives it
 * ({@link mountDshClient}). Anything that needs the kernel before then (the session bridge, a
 * `?conversation=` link) waits for that element; on a page that never shows the app (sign-in, a
 * credential form) nothing boots.
 *
 * Chat requires this client: every conversation is read from and sent into its native session
 * through the bridge it publishes. So a failed boot is not contained — it rejects with the
 * reason, and the cached attempt is dropped so the next caller boots again rather than inheriting a
 * permanent failure.
 */
export interface DshClientHandle {
  /** The booted Cordis context: the client plugins' service container. */
  readonly context: unknown;
  /** Dispose the plugin tree. */
  dispose(): Promise<void>;
}

let provideMount: ((mount: HTMLElement) => void) | undefined;
const mounted = new Promise<HTMLElement>(resolve => { provideMount = resolve; });
let booting: Promise<DshClientHandle> | undefined;

/**
 * Boot the harness client into the page's app element.
 *
 * @param mount - the element the harness renders the app into; only the first call's element is used.
 * @returns the booted client; rejects with the reason it did not come up.
 */
export function mountDshClient(mount: HTMLElement): Promise<DshClientHandle> {
  provideMount?.(mount);
  provideMount = undefined;
  return bootDshClient();
}

/**
 * The harness client, once the app has given it an element and it has booted there.
 *
 * Single-flight while a boot is in progress: a second boot would register every bundle twice.
 *
 * @returns the booted client; rejects with the reason it did not come up.
 */
export function bootDshClient(): Promise<DshClientHandle> {
  const attempt = booting ??= start();
  attempt.catch(() => { if (booting === attempt) booting = undefined; });
  return attempt;
}

async function start(): Promise<DshClientHandle> {
  const mount = await mounted;
  try {
    const module = await import("../../../harness/in-app");
    return await module.bootDshClientInApp({ mount });
  } catch (reason) {
    console.error("orik web: the harness client did not boot", reason);
    throw new Error("orik web: the session client did not start; conversations cannot be opened until it does", { cause: reason });
  }
}
